import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";
import { Users, Activity, DollarSign, ShoppingBag, UsersRound, Search, Plus } from "lucide-react";

export default function Admin() {
    const { t } = useLang();
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [teams, setTeams] = useState([]);
    const [tab, setTab] = useState("users");
    const [search, setSearch] = useState("");
    const [tokenInputs, setTokenInputs] = useState({});
    const [addingTokensFor, setAddingTokensFor] = useState(null);

    const refreshUsers = () => api.get("/admin/users").then((r) => setUsers(r.data.users));

    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data));
        refreshUsers();
        api.get("/admin/sessions").then((r) => setSessions(r.data.sessions));
        api.get("/admin/teams").then((r) => setTeams(r.data.teams || []));
    }, []);

    const filteredUsers = users.filter((u) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return u.email.toLowerCase().includes(q) || u.name.toLowerCase().includes(q);
    });

    const addTokens = async (userId) => {
        const raw = tokenInputs[userId];
        const amount = parseInt(raw, 10);
        if (!amount) {
            toast.error(t("admin.tokens_invalid"));
            return;
        }
        setAddingTokensFor(userId);
        try {
            await api.post(`/admin/users/${userId}/add-tokens`, { amount });
            toast.success(t("admin.tokens_added"));
            setTokenInputs((prev) => ({ ...prev, [userId]: "" }));
            await refreshUsers();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("common.error"));
        } finally {
            setAddingTokensFor(null);
        }
    };

    return (
        <div className="min-h-screen bg-ink text-slate-900">
            <Navbar />
            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
                <div className="mono-label mb-3">/ ADMIN</div>
                <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-12" data-testid="admin-title">
                    {t("admin.title")}
                </h1>

                {stats && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-100 border border-slate-200 mb-12">
                        <Stat icon={<Users size={14} />} label={t("admin.users")} value={stats.users} testid="admin-stat-users" />
                        <Stat icon={<Activity size={14} />} label={t("admin.sessions")} value={stats.sessions} testid="admin-stat-sessions" />
                        <Stat icon={<ShoppingBag size={14} />} label={t("admin.paid")} value={stats.paid_transactions} testid="admin-stat-paid" />
                        <Stat icon={<DollarSign size={14} />} label={t("admin.revenue")} value={`$${stats.revenue_usd}`} accent testid="admin-stat-revenue" />
                    </div>
                )}

                {stats && (
                    <div className="mb-12">
                        <div className="mono-label mb-4">/ {t("admin.by_plan")}</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-by-plan">
                            {stats.users_by_plan.map((p) => (
                                <div key={p.plan} className="card-surface p-4">
                                    <div className="mono-label mb-1">{p.plan}</div>
                                    <div className="font-display text-2xl">{p.count}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex gap-3 mb-6">
                    {["users", "sessions", "teams"].map((k) => (
                        <button
                            key={k}
                            onClick={() => setTab(k)}
                            className={`mono-label px-4 py-2 border transition-colors ${tab === k ? "border-brand text-brand" : "border-slate-300 text-slate-600"}`}
                            data-testid={`admin-tab-${k}`}
                        >
                            {k.toUpperCase()}
                        </button>
                    ))}
                </div>

                {tab === "users" && (
                    <>
                        <div className="relative mb-4 max-w-sm">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t("admin.search_placeholder")}
                                className="w-full bg-transparent border border-slate-300 pl-9 pr-3 py-2 text-sm rounded-sm focus:border-brand outline-none transition-colors"
                                data-testid="admin-users-search"
                            />
                        </div>
                        <div className="border border-slate-200 divide-y divide-slate-200" data-testid="admin-users-list">
                            {filteredUsers.length === 0 && (
                                <p className="px-6 py-4 text-slate-500 text-sm" data-testid="admin-users-empty">{t("admin.no_results")}</p>
                            )}
                            {filteredUsers.map((u) => (
                                <div key={u.id} className="px-6 py-4 grid grid-cols-12 gap-4 items-center" data-testid={`admin-user-row-${u.id}`}>
                                    <div className="col-span-3">
                                        <div className="font-display">{u.name}</div>
                                        <div className="text-sm text-slate-500">{u.email}</div>
                                    </div>
                                    <div className="col-span-1 mono-label">{u.plan}</div>
                                    <div className="col-span-1 mono-label">{u.role}</div>
                                    <div className="col-span-2 font-mono text-brand">{u.tokens} tokens</div>
                                    <div className="col-span-1 font-mono text-slate-600">
                                        {u.avg_score != null ? `${u.avg_score}/100` : "—"}
                                    </div>
                                    <div className="col-span-1 text-xs text-slate-500">{new Date(u.created_at).toLocaleDateString()}</div>
                                    <div className="col-span-3 flex items-center gap-2">
                                        <input
                                            type="number"
                                            value={tokenInputs[u.id] || ""}
                                            onChange={(e) => setTokenInputs((prev) => ({ ...prev, [u.id]: e.target.value }))}
                                            placeholder={t("admin.tokens_placeholder")}
                                            className="w-20 bg-transparent border border-slate-300 px-2 py-1.5 text-sm rounded-sm focus:border-brand outline-none transition-colors"
                                            data-testid={`admin-tokens-input-${u.id}`}
                                        />
                                        <button
                                            onClick={() => addTokens(u.id)}
                                            disabled={addingTokensFor === u.id}
                                            className="btn-ghost text-xs px-2 py-1.5"
                                            data-testid={`admin-tokens-add-btn-${u.id}`}
                                        >
                                            <Plus size={12} /> {t("admin.tokens_add")}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {tab === "sessions" && (
                    <div className="border border-slate-200 divide-y divide-slate-200" data-testid="admin-sessions-list">
                        {sessions.map((s) => (
                            <div key={s.id} className="px-6 py-4 grid grid-cols-12 gap-4 items-center">
                                <div className="col-span-3 mono-label">{new Date(s.started_at).toLocaleString()}</div>
                                <div className="col-span-2 font-display">{s.scenario}</div>
                                <div className="col-span-2 mono-label">{s.language}</div>
                                <div className="col-span-2 mono-label">{s.status}</div>
                                <div className="col-span-3 font-mono text-brand">
                                    {s.scores ? `${s.scores.overall}/100` : "—"}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "teams" && (
                    <div data-testid="admin-teams-list">
                        <div className="mono-label mb-4">/ {t("admin.teams_title")}</div>
                        {teams.length === 0 && (
                            <p className="text-slate-500" data-testid="admin-no-teams">{t("admin.no_teams")}</p>
                        )}
                        {teams.length > 0 && (
                            <div className="border border-slate-200 divide-y divide-slate-200">
                                {teams.map((team) => (
                                    <div key={team.owner_id} className="px-6 py-4 grid grid-cols-12 gap-4 items-center" data-testid={`admin-team-${team.owner_id}`}>
                                        <div className="col-span-4">
                                            <div className="font-display flex items-center gap-2">
                                                <UsersRound size={14} className="text-brand" /> {team.owner_name}
                                            </div>
                                            <div className="text-sm text-slate-500">{team.owner_email}</div>
                                        </div>
                                        <div className="col-span-3 mono-label text-slate-600">
                                            {t("admin.invited_accounts")}: {team.invited_accounts}/{team.max_invited_accounts}
                                        </div>
                                        <div className="col-span-3 mono-label text-slate-600">{team.team_sessions} sesiones</div>
                                        <div className="col-span-2 font-mono text-brand">
                                            {team.team_avg_score != null ? `${team.team_avg_score}/100` : "—"}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

function Stat({ icon, label, value, accent, testid }) {
    return (
        <div className={`bg-ink p-6 ${accent ? "border-l-2 border-l-brand" : ""}`} data-testid={testid}>
            <div className="mono-label mb-3 flex items-center gap-2">{icon} {label}</div>
            <div className={`font-display text-4xl ${accent ? "text-brand" : ""}`}>{value}</div>
        </div>
    );
}
